import clsx from 'clsx';
import React, { InputHTMLAttributes, ReactNode, useState } from 'react';

import styles from './Input.module.scss';

export interface InputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'prefix' | 'size'> {
  ref?: React.Ref<HTMLInputElement>;
  size?: 'medium' | 'large';
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  hasError?: boolean;
  errorText?: ReactNode;
  helperText?: ReactNode;
  rightAlignText?: boolean;
  disabled?: boolean;
  prefix?: ReactNode;
  label?: ReactNode;
  'data-testid'?: string;
  variant?: 'fill' | 'outline';
  rows?: number;
  as?: 'input' | 'textarea';
}

export const Input = ({
  size = 'large',
  leftIcon,
  rightIcon,
  hasError,
  errorText,
  helperText,
  rightAlignText,
  label,
  id,
  className,
  prefix,
  disabled = false,
  variant = 'fill',
  rows,
  as: Component = 'input',
  ref,
  ...rest
}: InputProps) => {
  const [focused, setFocused] = useState(false);

  const onFocus = () => {
    setFocused(true);
  };

  const onBlur = () => {
    setFocused(false);
  };

  return (
    <div
      data-input-container
      ref={ref}
      className={clsx(styles.container, className)}
      data-testid="input-container"
    >
      {label && (
        <label data-input-label htmlFor={id} className={styles.styledLabel}>
          {label}
        </label>
      )}
      <div
        data-input-wrapper
        onFocus={onFocus}
        onBlur={onBlur}
        data-testid="input-wrapper"
        className={clsx(styles.inputWrapper, styles[variant], {
          [styles.error]: hasError,
          [styles.focused]: focused,
          [styles.isDisabled]: disabled,
        })}
      >
        {leftIcon && (
          <span data-input-left-icon className={styles.leftIcon}>
            {leftIcon}
          </span>
        )}
        {prefix && (
          <div data-input-prefix className={styles.prefixContainer}>
            {prefix}
          </div>
        )}
        <input
          data-input-element
          className={clsx(styles.inputElement, styles[size], {
            [styles.rightAlignText]: rightAlignText || !!prefix,
            [styles.disabled]: disabled,
          })}
          id={id}
          {...(rows ? { rows } : {})}
          disabled={disabled}
          {...rest}
        />
        {rightIcon && (
          <span data-input-right-icon className={styles.rightIcon}>
            {rightIcon}
          </span>
        )}
      </div>
      {hasError && errorText && (
        <label
          data-input-error
          data-testid="error-text"
          className={styles.errorLabel}
        >
          {errorText}
        </label>
      )}
      {!hasError && helperText && (
        <label
          data-input-helper
          data-testid="helper-text"
          className={styles.helperLabel}
        >
          {helperText}
        </label>
      )}
    </div>
  );
};
